import React from "react";
import { motion } from "framer-motion";
import profileImage from "../images/about/profile.webp";
import CV from "../data/about/MatteoRobidouxCV.pdf";

import employments from "../data/portfolio/employments.json";
import degrees from "../data/portfolio/degrees.json";
import languages from "../data/portfolio/languages.json";

function About() {
	const coreSkills = [
		{ name: "JavaScript", level: 95 },
		{ name: "React", level: 95 },
		{ name: "Java", level: 90 },
		{ name: "Python", level: 88 },
		{ name: "Node.js", level: 85 },
		{ name: "SQL", level: 85 },
		{ name: "TypeScript", level: 82 },
		{ name: "Kotlin", level: 80 }
	];

	return (
		<div className="min-h-screen px-4 sm:px-6 lg:px-8 py-24">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				className="max-w-6xl mx-auto w-full"
			>
				<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 sm:mb-12 text-center text-white">
					About Me
				</h1>

				{/* Hero Section */}
				<div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ delay: 0.2 }}
						className="relative group mx-auto w-full max-w-sm"
					>
						<div className="absolute inset-0 bg-white/10 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
						<div className="relative rounded-2xl overflow-hidden border-2 border-white/20 group-hover:border-white/40 transition-colors">
							<img
								src={profileImage}
								alt="Matteo Robidoux"
								className="w-full h-auto object-cover"
							/>
						</div>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ delay: 0.4 }}
					>
						<h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
							I'm Matteo Robidoux,
						</h2>
						<p className="text-gray-300 text-base sm:text-lg mb-6 leading-relaxed">
							I recently graduated with a Bachelor's degree in Computer Science
							from Concordia University and am passionate about building
							reliable, scalable software that solves real-world problems. I
							have professional experience developing high-performance backend
							systems at TS Imagine, where I contributed to a globally used
							trading platform using Java and JavaScript. Beyond my professional
							experience, I enjoy building full-stack applications, exploring
							machine learning, and working with cloud and DevOps technologies
							such as Python, React, Docker, AWS, and Azure. I'm always looking
							to expand my skills, take on new technical challenges, and build
							software that makes a meaningful impact. I'm currently seeking
							full-time Software Developer opportunities where I can continue
							learning, collaborating, and contributing to innovative projects.
						</p>
						<a href={CV} download="Matteo_Robidoux_CV.pdf">
							<motion.button
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-white rounded-lg text-slate-900 text-sm sm:text-base font-semibold hover:bg-gray-100 hover:shadow-lg hover:shadow-white/20 transition-all duration-300 flex items-center justify-center gap-2"
							>
								<svg
									className="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
								Download CV
							</motion.button>
						</a>
					</motion.div>
				</div>

				{/* Core Skills */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.6 }}
					className="mb-12"
				>
					<h2 className="text-3xl font-bold text-white mb-8 text-center">
						Core Skills
					</h2>
					<div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
						{coreSkills.map((skill, index) => (
							<motion.div
								key={skill.name}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 0.7 + index * 0.05 }}
								className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-white/30 transition-colors"
							>
								<div className="flex justify-between mb-2">
									<span className="text-white font-medium">{skill.name}</span>
									<span className="text-gray-400">{skill.level}%</span>
								</div>
								<div className="h-2 bg-white/10 rounded-full overflow-hidden">
									<motion.div
										className="h-full bg-gradient-to-r from-white/60 to-white/90"
										initial={{ width: 0 }}
										animate={{ width: `${skill.level}%` }}
										transition={{ delay: 0.8 + index * 0.05, duration: 1 }}
									/>
								</div>
							</motion.div>
						))}
					</div>
				</motion.div>

				{/* Also Proficient In */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.7 }}
					className="mb-16"
				>
					<h2 className="text-2xl font-bold text-white mb-6 text-center">
						Also Proficient In
					</h2>
					<div className="flex flex-wrap gap-3 justify-center max-w-5xl mx-auto">
						{languages.map((language, index) => (
							<motion.div
								key={language.name}
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ delay: 0.8 + index * 0.02 }}
								className="px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:border-white/30 transition-all hover:scale-105"
							>
								<span className="text-white text-sm font-medium">
									{language.name}
								</span>
							</motion.div>
						))}
					</div>
				</motion.div>

				{/* Work Experience */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.8 }}
					className="mb-16"
				>
					<h2 className="text-3xl font-bold text-white mb-8 text-center">
						Work Experience
					</h2>
					<div className="space-y-6 max-w-4xl mx-auto">
						{employments.map((exp, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 1 + index * 0.1 }}
								className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/30 transition-colors"
							>
								<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
									<div>
										<h3 className="text-xl font-bold text-white">
											{exp.title} @ {exp.company}
										</h3>
										<p className="text-gray-400 text-sm mt-1">
											{exp.employment}
										</p>
									</div>
									<span className="text-gray-500 text-sm mt-2 sm:mt-0">
										{exp.duration}
									</span>
								</div>
								<p className="text-gray-300 mb-3">{exp.description}</p>
								<div className="flex flex-wrap gap-2">
									{exp.skills.split(", ").map((skill) => (
										<span
											key={skill}
											className="px-3 py-1 bg-white/10 border border-white/15 rounded-full text-gray-300 text-xs"
										>
											{skill}
										</span>
									))}
								</div>
							</motion.div>
						))}
					</div>
				</motion.div>

				{/* Education */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.9 }}
					className="mb-8"
				>
					<h2 className="text-3xl font-bold text-white mb-8 text-center">
						Education
					</h2>
					<div className="space-y-4 max-w-4xl mx-auto">
						{degrees.map((edu, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 1.2 + index * 0.1 }}
								className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/30 transition-colors"
							>
								<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
									<div>
										<h3 className="text-xl font-bold text-white">
											{edu.certificate}
										</h3>
										<p className="text-gray-400 mt-1">{edu.institute}</p>
									</div>
									<span className="text-gray-500 text-sm mt-2 sm:mt-0">
										{edu.duration}
									</span>
								</div>
							</motion.div>
						))}
					</div>
				</motion.div>
			</motion.div>
		</div>
	);
}

export default About;
